import { isMusicEnabled } from '../audio/music-preference';
import { t } from '../i18n/i18n';

/** Speaker with waves, and speaker with a cross. Two paths so only the right-hand half swaps. */
const SPEAKER_BODY = 'M4 9.5v5h3.2L11.5 18V6L7.2 9.5H4Z';
const WAVES = 'M14.6 8.8a4.2 4.2 0 0 1 0 6.4M17 6.4a7.6 7.6 0 0 1 0 11.2';
const CROSS = 'M14.8 9.6l5.2 4.8M20 9.6l-5.2 4.8';

function glyph(enabled: boolean): string {
  return `
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="${SPEAKER_BODY}" fill="currentColor" stroke="none" />
      <path d="${enabled ? WAVES : CROSS}" />
    </svg>`;
}

/**
 * Music on/off, persisted (`music-preference.ts`).
 *
 * A toggle, not a slider: there is no audio settings screen in this game yet,
 * and the one control music genuinely needs is the ability to stop it. A player
 * who wants it quieter rather than gone is not served by this, and that is the
 * known limit of the current scope.
 *
 * The button is `aria-pressed` rather than swapping its label, so a screen
 * reader announces the state instead of the next action — and the icon-only
 * face keeps an accessible name via `aria-label`.
 */
export class MusicToggle {
  private readonly button: HTMLButtonElement;
  private enabled: boolean;

  constructor(
    private readonly host: HTMLElement,
    private readonly onChange: (enabled: boolean) => void,
  ) {
    this.enabled = isMusicEnabled();
    this.button = document.createElement('button');
    // min-w-11/min-h-11 = the 44px tap target CLAUDE.md requires; an icon button
    // is exactly the case where the visual size would otherwise fall short.
    this.button.className = 'btn btn-stone min-w-11 min-h-11 px-3 flex items-center justify-center';
    this.button.type = 'button';
    this.button.addEventListener('click', this.toggle);
    this.host.appendChild(this.button);
    this.render();
  }

  private toggle = (): void => {
    this.enabled = !this.enabled;
    this.render();
    // Persisting is the engine's job (it also has to start/stop the bed), so the
    // preference write stays in one place rather than happening twice.
    this.onChange(this.enabled);
  };

  /** Repaint from outside (the settings Audio section moved the same preference). No onChange. */
  setEnabled(enabled: boolean): void {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    this.render();
  }

  private render(): void {
    this.button.innerHTML = glyph(this.enabled);
    this.button.setAttribute('aria-pressed', String(this.enabled));
    this.button.setAttribute('aria-label', t('Music'));
    this.button.title = this.enabled ? t('Music on') : t('Music off');
  }

  dispose(): void {
    this.button.removeEventListener('click', this.toggle);
    this.button.remove();
  }
}
